import { RouteObject } from 'react-router'
import React from 'react'
import { PageLayout } from '@/common/layouts/PageLayout.tsx'
import { Tabs, TabsProps, Typography } from 'antd'
import { FxRateHistoryConfiguration } from '@/pages/portal-configuration/fx-rate-history.tsx'
import { ProtectedRoutes } from '@/common/layouts/ProtectedRoutes.tsx'
import { PageTitle } from '@/common/components/PageTitle.tsx'
import { WorkableApiConfigTab } from '@/pages/portal-configuration/workable-api-config-tab.tsx'
import { EmployeeDataAccessClaimTab } from '@/pages/portal-configuration/employee-data-access-claim-tab.tsx'
import { useAuth } from '@/common/hooks/useAuth.ts'
import { useStateWithSearchParams } from '@/common/hooks/useStateWithSearchParams.ts'

type Tab = 'fxRateHistory' | 'workableApiConfig' | 'employeeDataAccess'

const PortalConfigurationPage: React.FC = () => {
  const { checkHasAllPermissions } = useAuth()
  const [activeTab, setActiveTab] = useStateWithSearchParams<Tab>('tab', 'fxRateHistory')

  // The route itself only needs read-portal-configuration, but the access claim endpoint is gated
  // on the edit permission: it exposes who can read employee data where, which is a narrower thing
  // to hand out than FX rates. Hide the tab rather than letting it render and 403.
  const canViewAccessClaims = checkHasAllPermissions([{
    moduleName: 'human-capital-portal',
    functionName: 'edit-portal-configuration',
  }])

  const items: TabsProps['items'] = [
    {
      label: 'FX Rate History',
      key: 'fxRateHistory',
      children: <FxRateHistoryConfiguration />,
    },
    {
      label: 'Workable API Config',
      key: 'workableApiConfig',
      children: <WorkableApiConfigTab />,
    },
    ...(canViewAccessClaims
      ? [{
        label: 'Employee Data Access',
        key: 'employeeDataAccess',
        children: <EmployeeDataAccessClaimTab />,
      }]
      : []),
  ]

  return (
    <PageLayout className="h-full">
      <PageTitle value="Portal Configuration" />
      <Typography.Title level={2}>Portal Configuration</Typography.Title>
      <Tabs activeKey={activeTab} onChange={key => setActiveTab(key as Tab)} items={items} />
    </PageLayout>
  )
}

export const portalConfigurationRoutes: RouteObject = {
  path: 'portal-configuration',
  element: <ProtectedRoutes
    requiredPermissions={[{
      moduleName: 'human-capital-portal',
      functionName: 'read-portal-configuration',
    }]}
  />,
  children: [
    { index: true, element: <PortalConfigurationPage /> },
  ],
}